/**
 * Care Pass checkout — sandbox capture or Razorpay Checkout (production).
 */

import api from "./api";
import { showToast } from "../utils/toast";

function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("Browser only"));
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay")));
      if (window.Razorpay) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });
}

async function waitForCarePassActive(patientId, { attempts = 8 } = {}) {
  for (let i = 0; i < attempts; i += 1) {
    const status = await api.getPatientSubscriptionStatus(patientId).catch(() => null);
    if (Boolean(status?.subscription?.active ?? status?.active)) return status;
    await new Promise((resolve) => setTimeout(resolve, 700 * (i + 1)));
  }
  return null;
}

/**
 * Pay for a Care Pass. Resolves when sandbox capture completes or Razorpay checkout succeeds.
 * Production: verifies payment with the backend (idempotent) before returning.
 */
export async function payCarePass({ patientId, planId, packId, renewal } = {}) {
  const orderRes = await api.createCarePassPaymentOrder({
    patientId,
    planId,
    packId,
    renewal,
  });
  const payload = orderRes?.data || orderRes;

  if (payload?.mockAllowed) {
    const purchase = payload?.purchase || payload;
    const activated = patientId ? await waitForCarePassActive(patientId) : null;
    return {
      mockCompleted: Boolean(payload?.mockCompleted),
      purchase,
      pendingConfirmation: !activated && !purchase?.subscriptionId,
      subscription: activated?.subscription || activated || purchase?.subscription || null,
      quote: payload?.quote || null,
    };
  }

  const order = payload?.order;
  const keyId = payload?.keyId;
  if (!order?.id || !keyId) {
    throw new Error("Payment order could not be created. Try again or contact support.");
  }

  await loadRazorpayScript();

  const razorpay = await new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: keyId,
      amount: order.amount,
      currency: order.currency || "INR",
      name: "NemaCare",
      description: "NEMA Care Pass",
      order_id: order.id,
      prefill: payload?.prefill || undefined,
      handler(response) {
        showToast.success("Payment received. Activating your Care Pass…");
        resolve(response);
      },
      modal: {
        ondismiss() {
          reject(new Error("Payment cancelled"));
        },
      },
    });
    rzp.on("payment.failed", (err) => {
      reject(new Error(err?.error?.description || "Payment failed"));
    });
    rzp.open();
  });

  // Activate immediately via signed checkout confirmation (idempotent with webhook).
  let purchase = null;
  try {
    const confirmRes = await api.confirmCarePassCheckoutPayment({
      patientId,
      planId,
      packId,
      renewal,
      razorpay_order_id: razorpay?.razorpay_order_id || order.id,
      razorpay_payment_id: razorpay?.razorpay_payment_id,
      razorpay_signature: razorpay?.razorpay_signature,
    });
    purchase = confirmRes?.transaction || confirmRes?.purchase || confirmRes;
  } catch (confirmErr) {
    // Fall back to webhook/poll if confirm is briefly unavailable.
    console.warn("[carePassPayment] confirm failed, polling status:", confirmErr?.message || confirmErr);
  }

  const activated = patientId ? await waitForCarePassActive(patientId) : null;
  return {
    razorpay,
    purchase,
    pendingConfirmation: !activated && !purchase?.subscriptionId,
    subscription: activated?.subscription || activated || purchase?.subscription || null,
    quote: payload?.quote || null,
  };
}

export async function payCarePassUpgrade({ patientId, subscriptionId, targetDays } = {}) {
  const orderRes = await api.createCarePassUpgradeOrder({ patientId, subscriptionId, targetDays });
  const payload = orderRes?.data || orderRes;

  if (payload?.mockAllowed) {
    return {
      mockCompleted: Boolean(payload?.mockCompleted),
      quote: payload?.quote || null,
      upgraded: payload?.upgraded || payload,
    };
  }

  const order = payload?.order;
  const keyId = payload?.keyId;
  if (!order?.id || !keyId) {
    throw new Error("Payment order could not be created. Try again or contact support.");
  }

  await loadRazorpayScript();

  const razorpay = await new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: keyId,
      amount: order.amount,
      currency: order.currency || "INR",
      name: "NemaCare",
      description: "Care Pass upgrade",
      order_id: order.id,
      prefill: payload?.prefill || undefined,
      handler(response) {
        showToast.success("Payment received. Upgrading your Care Pass…");
        resolve(response);
      },
      modal: {
        ondismiss() {
          reject(new Error("Payment cancelled"));
        },
      },
    });
    rzp.on("payment.failed", (err) => {
      reject(new Error(err?.error?.description || "Payment failed"));
    });
    rzp.open();
  });

  const confirmRes = await api.confirmCarePassUpgradePayment({
    patientId,
    subscriptionId,
    targetDays,
    razorpay_order_id: razorpay?.razorpay_order_id || order.id,
    razorpay_payment_id: razorpay?.razorpay_payment_id,
    razorpay_signature: razorpay?.razorpay_signature,
  });
  return {
    razorpay,
    quote: payload?.quote || confirmRes?.quote,
    upgraded: confirmRes?.upgraded || confirmRes,
  };
}
